/**
 * WorkingHoursSection Component
 *
 * Component for displaying and editing working hours by day of week
 * Uses proper time fields for database queries (finding available workers)
 * Based on Figma design: node-id=1-1448
 */

'use client';

import { useCallback } from 'react';

export interface WorkingHoursDay {
  dayOfWeek: number; // 0-6 (Monday-Sunday)
  startTime: string; // "HH:mm" format, e.g. "09:00"
  endTime: string; // "HH:mm" format, e.g. "17:00"
  isActive: boolean;
}

export interface WorkingHoursSectionProps {
  workingHours: WorkingHoursDay[];
  onChange?: (hours: WorkingHoursDay[]) => void;
  editable?: boolean;
}

const DAYS_OF_WEEK = [
  { id: 0, name: 'Monday', isWeekday: true },
  { id: 1, name: 'Tuesday', isWeekday: true },
  { id: 2, name: 'Wednesday', isWeekday: true },
  { id: 3, name: 'Thursday', isWeekday: true },
  { id: 4, name: 'Friday', isWeekday: true },
  { id: 5, name: 'Saturday', isWeekday: false },
  { id: 6, name: 'Sunday', isWeekday: false },
];

/**
 * Generate default working hours:
 * - Weekdays (Mon-Fri): 09:00 - 17:00
 * - Weekends (Sat-Sun): inactive
 */
export function getDefaultWorkingHours(): WorkingHoursDay[] {
  return DAYS_OF_WEEK.map((day) => ({
    dayOfWeek: day.id,
    startTime: day.isWeekday ? '09:00' : '09:00',
    endTime: day.isWeekday ? '17:00' : '17:00',
    isActive: day.isWeekday,
  }));
}

/**
 * Format time range for display
 */
function formatTimeRange(startTime: string, endTime: string): string {
  return `${startTime}-${endTime}`;
}

export function WorkingHoursSection({
  workingHours,
  onChange,
  editable = false,
}: WorkingHoursSectionProps) {
  const getDayData = useCallback(
    (dayOfWeek: number): WorkingHoursDay => {
      const dayHours = workingHours.find((wh) => wh.dayOfWeek === dayOfWeek);
      return (
        dayHours ?? {
          dayOfWeek,
          startTime: '09:00',
          endTime: '17:00',
          isActive: false,
        }
      );
    },
    [workingHours]
  );

  const handleDayToggle = useCallback(
    (dayOfWeek: number) => {
      if (!onChange) return;

      const newHours = DAYS_OF_WEEK.map((day) => {
        const existing = getDayData(day.id);
        if (day.id === dayOfWeek) {
          return { ...existing, isActive: !existing.isActive };
        }
        return existing;
      });
      onChange(newHours);
    },
    [onChange, getDayData]
  );

  const handleTimeChange = useCallback(
    (dayOfWeek: number, field: 'startTime' | 'endTime', value: string) => {
      if (!onChange) return;

      const newHours = DAYS_OF_WEEK.map((day) => {
        const existing = getDayData(day.id);
        if (day.id === dayOfWeek) {
          return { ...existing, [field]: value };
        }
        return existing;
      });
      onChange(newHours);
    },
    [onChange, getDayData]
  );

  // Display-only mode (matches Figma design)
  if (!editable) {
    return (
      <div className="w-80 self-stretch p-5 bg-background-tertiary rounded-[10px] inline-flex flex-col justify-start items-start gap-5">
        <div className="self-stretch text-text-primary text-xl font-medium font-roboto leading-6">
          Working Hours:
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
          {DAYS_OF_WEEK.map((day) => {
            const { startTime, endTime, isActive } = getDayData(day.id);
            const displayHours = isActive ? formatTimeRange(startTime, endTime) : 'Unavailable';
            const textColorClass = isActive ? 'text-accent-primary' : 'text-text-secondary';
            const dayTextColorClass = isActive ? 'text-text-primary' : 'text-text-secondary';

            return (
              <div key={day.id} className="self-stretch inline-flex justify-between items-start">
                <div className={`${dayTextColorClass} text-xl font-medium font-roboto leading-6`}>
                  {day.name}
                </div>
                <div className={`${textColorClass} text-xl font-medium font-roboto leading-6`}>
                  {displayHours}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Editable mode
  return (
    <div className="w-full p-5 bg-background-tertiary rounded-[10px] flex flex-col gap-5">
      <div className="text-text-primary text-xl font-medium font-roboto leading-6">
        Working Hours:
      </div>
      <div className="flex flex-col gap-3">
        {DAYS_OF_WEEK.map((day) => {
          const { startTime, endTime, isActive } = getDayData(day.id);

          return (
            <div key={day.id} className="flex items-center gap-4">
              {/* Day toggle */}
              <label className="flex items-center gap-2 w-32 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={() => handleDayToggle(day.id)}
                  className="w-4 h-4 rounded border-border-primary text-accent-primary focus:ring-accent-primary"
                />
                <span
                  className={`text-base font-medium ${isActive ? 'text-text-primary' : 'text-text-secondary'}`}
                >
                  {day.name}
                </span>
              </label>

              {/* Time inputs */}
              {isActive ? (
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => handleTimeChange(day.id, 'startTime', e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-border-primary bg-background-secondary text-text-primary text-sm focus:outline-none focus:border-accent-primary"
                  />
                  <span className="text-text-secondary">-</span>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => handleTimeChange(day.id, 'endTime', e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-border-primary bg-background-secondary text-text-primary text-sm focus:outline-none focus:border-accent-primary"
                  />
                </div>
              ) : (
                <span className="text-text-secondary text-sm">Unavailable</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
